/**
 * 团队文件夹配额（接口4 /web/workgroup/folder/ai/folder/get/quota）
 *
 * used/cap 统计的是团队全部文件夹（普通 + AI 智能分类，含子文件夹），不只
 * AI 模块；消费方包括 AI 分类页面 widget、全局创建卡点（useFolderCreateGate，
 * FoldersPicker / photoFolders / AI 分类共用），故挂在 store 顶层而非 aiClassify。
 *
 * 档位判定由组件用规范 vip_kind 做，本 slice 不存 member_kind。另存接口4 的
 * tiers 全档位表（kind/cap/label），供开通弹窗文案/tab 小字按服务端值展示
 * （去前端写死）。任何失败（含 500/解析失败）→ error=true：widget 隐藏
 * （fail-closed），创建卡点放行（fail-open，超限由后端创建接口兜底）；
 * tiers 缺失则消费方回落硬编码。
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import fetchHelper from '../utils/fetchHelper'
import { captureFetchException } from '../utils/sentryUtils'
import { RootState } from '../app/store'
import type { TierInfo } from '../pages/components/FolderPaywall/folderTiers'

declare const __API_SERVER_HOST__: string

interface QuotaState {
  used: number
  cap: number
  /** 接口4 全档位有序表（缺失为空数组，消费方回落硬编码） */
  tiers: TierInfo[]
  /** 当前数据所属团队 · 切团队后用于判断需要重拉（见 useFolderCreateGate） */
  groupID: string
  loading: boolean
  error: boolean
  loaded: boolean
}

const initialState: QuotaState = {
  used: 0,
  cap: 0,
  tiers: [],
  groupID: '',
  loading: false,
  error: false,
  loaded: false
}

/**
 * 从接口4 响应里容错取 used/cap/tiers。
 * 接口4 文档示例是扁平 {used,cap,member_kind,tiers}，但同 BFF 其它接口是
 * {code,data:{...}} 包裹，故三种层级都兜；used/cap 任一非有限数 → 返回 null
 * （按失败处理）。tiers 非数组时回落空数组（消费方再回落硬编码）。
 */
function pickQuota(
  res: any
): { used: number; cap: number; tiers: TierInfo[] } | null {
  const node =
    res && res.used !== undefined
      ? res
      : res && res.data && res.data.used !== undefined
        ? res.data
        : res && res.data && res.data.data && res.data.data.used !== undefined
          ? res.data.data
          : null
  if (!node) return null
  const used = Number(node.used)
  const cap = Number(node.cap)
  if (!Number.isFinite(used) || !Number.isFinite(cap)) return null
  const tiers: TierInfo[] = Array.isArray(node.tiers) ? node.tiers : []
  return { used, cap, tiers }
}

export const fetchFolderQuota = createAsyncThunk<
  { used: number; cap: number; tiers: TierInfo[] },
  { groupID: string },
  { state: RootState; rejectValue: string }
>(
  'folderQuota/fetch',
  async ({ groupID }, { dispatch, getState, rejectWithValue }) => {
    const config = {
      url: __API_SERVER_HOST__ + '/web/workgroup/folder/ai/folder/get/quota',
      arguments: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupID })
      }
    }
    try {
      const res = await fetchHelper(dispatch, getState, config)
      const picked = pickQuota(res)
      if (!picked) return rejectWithValue('quota parse failed')
      return picked
    } catch (err) {
      // strict + TS 4.5 下 catch 变量类型为 unknown，而 captureFetchException 形参是
      // TError（Error|IResult|IData），unknown 不可直接赋值，故在调用点断言为 any。
      captureFetchException(err as any, config)
      return rejectWithValue('quota request failed')
    }
  }
)

const folderQuotaSlice = createSlice({
  name: 'folderQuota',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolderQuota.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchFolderQuota.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.loaded = true
        state.groupID = action.meta.arg.groupID
        state.used = action.payload.used
        state.cap = action.payload.cap
        state.tiers = action.payload.tiers
      })
      .addCase(fetchFolderQuota.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.loaded = true
        // 失败也记录归属团队，避免 useFolderCreateGate 的自取 effect 反复重试
        state.groupID = action.meta.arg.groupID
      })
  }
})

/** 配额子状态 selector */
export const selectFolderQuota = (s: RootState): QuotaState => s.folderQuota

export default folderQuotaSlice.reducer
